import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

import type { HierarchyTree, HierarchyTreeRequest } from '~/server_shared/openapi/data-contracts';
import { getNotifyUserMessage } from '~/Utils/errors/notifyUserMessage';

import { reportAxiosError } from '../../../Utils';

import type { HierarchyIndex } from './hierarchyTree';
import { indexTree } from './hierarchyTree';

const HIERARCHY_TREE_ROUTE = '/api/v1/hierarchy/tree';

// The route answers every source failure as a 400 carrying a CMS-owned message, so this covers only
// the failures that never reached it -- the browser offline, a 500 in CMS itself. Rendering nothing
// instead would show the empty-tree state, which the design deliberately distinguishes from an
// outage.
const HIERARCHY_UNAVAILABLE_MESSAGE = 'Error retrieving the hierarchy, please refer to your IT team';

export interface UseHierarchyTreeArgs {
  groupId: string;
  /** Claim page. Exactly one of `claimId` / `policy` -- the request model 400s on both or neither. */
  claimId?: number;
  /** FNOL. The policy object the form already holds, which may be a `ManualPolicyShell`. */
  policy?: Record<string, unknown>;
  /** Flips true when the user opens the group. Nothing is fetched before that. */
  enabled: boolean;
}

export interface UseHierarchyTreeResult {
  index: HierarchyIndex | null;
  isLoading: boolean;
  /** The CMS message the route returned; already display-ready. */
  error: string | null;
  /** Fetched successfully and the tree is empty -- an empty state, not an error. */
  isEmpty: boolean;
  /**
   * Fetch again. The host offers this from the error state: the inline FNOL card never closes the
   * group, so reopening -- the claim page's retry -- is not available there and one transient
   * failure would otherwise strand the group for the whole mount.
   */
  retry: () => void;
}

interface TreeState {
  index: HierarchyIndex | null;
  error: string | null;
  isEmpty: boolean;
}

const NOTHING_FETCHED: TreeState = { index: null, error: null, isEmpty: false };

/**
 * Fetch one field group's hierarchy tree, on demand and once.
 *
 * The whole tree arrives in a single call and is filtered locally, so there is no per-keystroke
 * request and no per-level request. Successful results are cached for the lifetime of the mount,
 * keyed by what the answer depends on, so closing and reopening the group does not refetch. A
 * failure is deliberately not cached, so both ways back into the fetch retry it: reopening a closed
 * group, and the explicit `retry` the error state offers on a host that never closes one.
 */
export function useHierarchyTree({ groupId, claimId, policy, enabled }: UseHierarchyTreeArgs): UseHierarchyTreeResult {
  const [state, setState] = useState<TreeState>(NOTHING_FETCHED);
  const [isLoading, setIsLoading] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const cacheRef = useRef<Map<string, TreeState>>(new Map());

  // `policy` is a fresh object on most renders, so the effect keys off what the answer actually
  // depends on and reads the current request payload from a ref.
  const request = useMemo<HierarchyTreeRequest>(
    () => (claimId === undefined ? { group_id: groupId, policy } : { group_id: groupId, claim_id: claimId }),
    [groupId, claimId, policy]
  );
  const requestRef = useRef(request);
  requestRef.current = request;
  // Serialized only when an input changes: the FNOL form re-renders on every keystroke while the
  // policy it passes keeps its reference.
  const cacheKey = useMemo(() => JSON.stringify(request), [request]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setState(cached);
      return undefined;
    }

    let isCancelled = false;
    setState(NOTHING_FETCHED);
    setIsLoading(true);

    const fetchTree = async () => {
      let next: TreeState;
      try {
        const { data } = await axios.post<HierarchyTree>(HIERARCHY_TREE_ROUTE, requestRef.current);
        next = { index: indexTree(data ?? {}), error: null, isEmpty: (data?.nodes ?? []).length === 0 };
        cacheRef.current.set(cacheKey, next);
      } catch (error) {
        const notifyUserMessage = getNotifyUserMessage(error);
        if (notifyUserMessage === null) {
          await reportAxiosError(error);
        }
        next = { index: null, error: notifyUserMessage ?? HIERARCHY_UNAVAILABLE_MESSAGE, isEmpty: false };
      }

      if (isCancelled) {
        return;
      }
      setState(next);
      setIsLoading(false);
    };

    void fetchTree();

    // Clearing the flag here and not only on a settled fetch is what keeps a cancelled one from
    // stranding it: closing the group mid-flight re-runs this effect, and a reopen that then hits
    // the cached early return above would never reach the `setIsLoading(false)` after the await,
    // leaving every level disabled behind a spinner. React runs this before the next effect body,
    // so a re-run that starts its own fetch still ends up loading.
    return () => {
      isCancelled = true;
      setIsLoading(false);
    };
  }, [enabled, cacheKey, attempt]);

  const retry = useCallback(() => setAttempt((previous) => previous + 1), []);

  return { ...state, isLoading, retry };
}
